import { useEffect, useState, useCallback, useMemo } from "react";
import api, { API, getToken } from "@/lib/api";
import { STATUSES, FINISHES, CATEGORIES, fmtDate, fmtDateTime } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", status: "all", finish: "all", category: "all", staff_id: "all" });

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    if (filters.status !== "all") p.set("status", filters.status);
    if (filters.finish !== "all") p.set("finish", filters.finish);
    if (filters.category !== "all") p.set("category", filters.category);
    if (filters.staff_id !== "all") p.set("staff_id", filters.staff_id);
    return p;
  }, [filters]);

  const loadStats = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    api.get(`/admin/stats?${p.toString()}`).then((r) => setStats(r.data)).catch((e) => console.error("Failed to load stats:", e));
  }, [filters.date_from, filters.date_to]);

  const loadRows = useCallback(() => {
    setLoadingRows(true);
    api.get(`/reports/jobs?format=json&${params().toString()}`)
      .then((r) => setRows(r.data.rows))
      .catch(() => toast.error("Failed to load report"))
      .finally(() => setLoadingRows(false));
  }, [params]);

  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch((e) => console.error("Failed to load staff:", e)); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { const t = setTimeout(loadRows, 300); return () => clearTimeout(t); }, [loadRows]);

  const exportFile = (fmt) => {
    const p = params();
    const token = getToken();
    if (token) p.set("auth", token);
    window.open(`${API}/reports/jobs?format=${fmt}&${p.toString()}`, "_blank");
  };

  const finishEntries = useMemo(
    () => (stats ? Object.entries(stats.finish_counts).sort((a, b) => b[1] - a[1]) : []),
    [stats]
  );

  const statCards = stats ? [
    { label: "Total Jobs", value: stats.total_jobs, testId: "stat-total-jobs" },
    { label: "New", value: stats.status_counts["New"] || 0, testId: "stat-new-jobs" },
    { label: "In Processing", value: ["Processing 1", "Processing 2"].reduce((a, s) => a + (stats.status_counts[s] || 0), 0), testId: "stat-processing-jobs" },
    { label: "Ready", value: stats.status_counts["Ready"] || 0, testId: "stat-ready-jobs" },
    { label: "Delivered", value: stats.status_counts["Delivered"] || 0, testId: "stat-delivered-jobs" },
    { label: "Cancelled", value: stats.status_counts["Cancelled"] || 0, testId: "stat-cancelled-jobs" },
    { label: "Total Customers", value: stats.total_customers, testId: "stat-total-customers" },
    { label: "New Customers", value: stats.new_customers, testId: "stat-new-customers" },
    { label: "Repeat Customers", value: stats.repeat_customers, testId: "stat-repeat-customers" },
  ] : [];

  return (
    <div data-testid="reports-page">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Reports & Analytics</h1>

      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div><Label className="text-xs">From</Label><Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-10 mt-1" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-10 mt-1" /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="filter-status" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Finish</Label>
              <Select value={filters.finish} onValueChange={(v) => setFilters({ ...filters, finish: v })}>
                <SelectTrigger data-testid="filter-finish" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{FINISHES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Category</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger data-testid="filter-category" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Staff</Label>
              <Select value={filters.staff_id} onValueChange={(v) => setFilters({ ...filters, staff_id: v })}>
                <SelectTrigger data-testid="filter-staff" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        </CardContent>
      </Card>

      {stats && (
        <>
          <h2 className="text-base md:text-lg font-semibold mt-6 mb-3">Business Statistics</h2>
          <div className="grid grid-cols-3 lg:grid-cols-9 gap-3" data-testid="admin-stats-grid">
            {statCards.map((s) => (
              <Card key={s.label} data-testid={s.testId}>
                <CardContent className="p-3 text-center">
                  <p className="font-display text-xl sm:text-2xl font-bold">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground font-medium">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Card>
              <CardContent className="p-4 sm:p-5">
                <h3 className="text-sm font-semibold mb-3">Finish-wise Job Count</h3>
                <div className="space-y-2" data-testid="finish-stats">
                  {finishEntries.map(([f, n]) => (
                    <div key={f} className="flex items-center gap-2 text-sm">
                      <span className="w-32 truncate">{f}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (n / (stats.total_jobs || 1)) * 100)}%` }} />
                      </div>
                      <span className="text-xs font-semibold w-6 text-right">{n}</span>
                    </div>
                  ))}
                  {Object.keys(stats.finish_counts).length === 0 && <p className="text-xs text-muted-foreground">No data yet.</p>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 sm:p-5">
                <h3 className="text-sm font-semibold mb-3">Staff Performance</h3>
                <Table data-testid="staff-performance-table">
                  <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead className="text-right">Created</TableHead><TableHead className="text-right">Assigned</TableHead><TableHead className="text-right">Completed</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {stats.staff_performance.map((s) => (
                      <TableRow key={s.user_id} data-testid={`perf-${s.user_id}`}>
                        <TableCell><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.role}{!s.active ? " · inactive" : ""}</div></TableCell>
                        <TableCell className="text-right">{s.jobs_created}</TableCell>
                        <TableCell className="text-right">{s.jobs_assigned}</TableCell>
                        <TableCell className="text-right">{s.jobs_completed}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <div className="flex items-center justify-between mt-6 mb-3 flex-wrap gap-2">
        <h2 className="text-base md:text-lg font-semibold">Jobs Report ({rows.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportFile("csv")} data-testid="export-csv-button"><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="export-excel-button"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
        </div>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <Table data-testid="report-table">
            <TableHeader>
              <TableRow>
                <TableHead>Job No</TableHead><TableHead>Customer</TableHead><TableHead>Article</TableHead>
                <TableHead>Finish</TableHead><TableHead>Status</TableHead><TableHead>Assigned</TableHead>
                <TableHead>Created</TableHead><TableHead>Est. Value</TableHead><TableHead>Advance</TableHead><TableHead>Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.job_number} data-testid={`report-row-${r.job_number}`}>
                  <TableCell className="font-semibold whitespace-nowrap">{r.job_number}</TableCell>
                  <TableCell><div>{r.customer_name}</div><div className="text-xs text-muted-foreground">{r.customer_mobile}</div></TableCell>
                  <TableCell>{r.article_name}</TableCell>
                  <TableCell className="text-xs">{r.required_finish}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-xs">{r.assigned_staff || "—"}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell className="text-xs">{r.estimated_value !== "" ? `₹${r.estimated_value}` : "—"}</TableCell>
                  <TableCell className="text-xs">{r.advance_payment !== "" ? `₹${r.advance_payment}` : "—"}</TableCell>
                  <TableCell className="text-xs font-medium" data-testid={`report-balance-${r.job_number}`}>{r.balance_payment !== "" ? `₹${r.balance_payment}` : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {loadingRows && <p className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</p>}
        {!loadingRows && rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground" data-testid="report-empty">No jobs match the selected filters.</p>}
      </Card>
    </div>
  );
}
